"use client";

import * as SunCalc from "suncalc";
import { mergeClasses } from "@huub-web/utils";
import { DateField, Icon, Text, type ClassNameProp } from "@huub-web/ui";

import {
  sliderContainerStyle,
  timelineStyle,
  labelsContainerStyle,
  labelStyle,
  sliderInputStyle,
  dateFieldContainerStyle,
  hourMarkersContainerStyle,
  hourMarkerStyle,
  timeLabelStyle,
  timeLabelContainerStyle,
  timeLabelIconStyle,
} from "./SundialSlider.css";
import { theme } from "@huub-web/ui/theme/css/theme.css";
import { elevations } from "@huub-web/ui/theme/css/elevations.css";

export type SundialSliderProps = ClassNameProp & {
  /**
   * Current time value (0-23)
   */
  value: number;
  /**
   * Location coordinates [latitude, longitude] for sunrise/sunset calculation
   */
  location: [number, number];
  /**
   * Current date for sunrise/sunset calculation
   */
  date: Date;
  /**
   * Date value for DateField
   */
  selectedDate: Date;
  /**
   * Callback when date changes
   */
  onDateChange: (date: string) => void /**
   * Callback when time changes
   */;
  onTimeChange: (hour: number, minutes: number) => void;
};

/**
 * SundialSlider - A custom slider that visualizes day/night cycles with sunrise and sunset times
 */
export function SundialSlider({
  value,
  location,
  date,
  selectedDate,
  onTimeChange,
  onDateChange,
  className,
}: SundialSliderProps) {
  // Calculate sunrise and sunset times using SunCalc
  const sunTimes = SunCalc.getTimes(date, location[0], location[1]);
  const sunriseHour =
    sunTimes.sunrise.getHours() + sunTimes.sunrise.getMinutes() / 60;
  const sunsetHour =
    sunTimes.sunset.getHours() + sunTimes.sunset.getMinutes() / 60;

  // Create gradient background showing day/night periods
  const backgroundGradient = `linear-gradient(to right, 
    ${theme.colors.background} 0%, 
    ${theme.colors.background} ${(sunriseHour / 24) * 100}%, 
  #ffd700 ${(sunriseHour / 24) * 100}%, 
  #87ceeb ${((sunriseHour + sunsetHour) / 2 / 24) * 100}%, 
  #ffa500 ${(sunsetHour / 24) * 100}%, 
    ${theme.colors.background} ${(sunsetHour / 24) * 100}%, 
    ${theme.colors.background} 100%)`;

  const formatTime = (hour: number, minutes: number): string => {
    const hours = Math.floor(hour);
    // const minutes = Math.round((hour % 1) * 60);
    // console.log("hours", hours);
    // console.log("minutes", minutes);

    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const formatDateForInput = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
  };

  return (
    <>
      <div className={dateFieldContainerStyle}>
        <DateField
          value={formatDateForInput(selectedDate)}
          onChange={onDateChange}
          variant="default"
          inputFormat="yyyy-MM-dd"
          outputFormat="yyyy-MM-dd"
        />
      </div>

      <div className={mergeClasses(sliderContainerStyle, className)}>
        <div className={timeLabelContainerStyle}>
          <Text
            className={mergeClasses(timeLabelStyle(), elevations.m)}
            style={{ left: `${(value / 24) * 100}%` }}
          >
            <Icon icon="clock" size="s" className={timeLabelIconStyle} />
            {formatTime(selectedDate.getHours(), selectedDate.getMinutes())}
          </Text>
        </div>

        {/* Timeline with day/night visualization */}
        <div
          className={timelineStyle}
          style={{ background: backgroundGradient }}
        />

        {/* Hour markers */}
        <div className={hourMarkersContainerStyle}>
          {Array.from({ length: 25 }, (_, i) => (
            <div
              key={i}
              className={hourMarkerStyle}
              style={{ left: `${(i / 24) * 100}%` }}
            />
          ))}
        </div>

        {/* Time labels */}
        <div className={labelsContainerStyle}>
          <Text
            variant="bodyXS"
            className={labelStyle()}
            style={{ left: 0 }}
            color="currentColor50"
          >
            00:00
          </Text>
          <Icon
            icon={
              <>
                <path
                  d="M17.5 12C17.5 15.0376 15.0376 17.5 12 17.5C8.96243 17.5 6.5 15.0376 6.5 12C6.5 8.96243 8.96243 6.5 12 6.5C15.0376 6.5 17.5 8.96243 17.5 12Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 1C12.5523 1 13 1.44772 13 2V3.5C13 4.05228 12.5523 4.5 12 4.5C11.4477 4.5 11 4.05228 11 3.5V2C11 1.44772 11.4477 1 12 1ZM4.22149 4.22149C4.61202 3.83097 5.24518 3.83097 5.6357 4.22149L6.69636 5.28215C7.08689 5.67268 7.08689 6.30584 6.69636 6.69636C6.30584 7.08689 5.67268 7.08689 5.28215 6.69636L4.22149 5.6357C3.83097 5.24518 3.83097 4.61201 4.22149 4.22149ZM19.7784 4.2216C20.1689 4.61213 20.1689 5.24529 19.7784 5.63582L18.7177 6.69648C18.3272 7.087 17.694 7.087 17.3035 6.69648C16.913 6.30595 16.913 5.67279 17.3035 5.28226L18.3642 4.2216C18.7547 3.83108 19.3879 3.83108 19.7784 4.2216ZM1 12C1 11.4477 1.44772 11 2 11H3.5C4.05228 11 4.5 11.4477 4.5 12C4.5 12.5523 4.05228 13 3.5 13H2C1.44772 13 1 12.5523 1 12ZM19.5 12C19.5 11.4477 19.9477 11 20.5 11H22C22.5523 11 23 11.4477 23 12C23 12.5523 22.5523 13 22 13H20.5C19.9477 13 19.5 12.5523 19.5 12ZM17.303 17.3035C17.6936 16.913 18.3267 16.913 18.7172 17.3035L19.7779 18.3642C20.1684 18.7547 20.1684 19.3879 19.7779 19.7784C19.3874 20.1689 18.7542 20.1689 18.3637 19.7784L17.303 18.7177C16.9125 18.3272 16.9125 17.694 17.303 17.3035ZM6.69685 17.3036C7.08738 17.6942 7.08738 18.3273 6.69685 18.7178L5.63619 19.7785C5.24567 20.169 4.6125 20.169 4.22198 19.7785C3.83145 19.388 3.83145 18.7548 4.22198 18.3643L5.28264 17.3036C5.67316 16.9131 6.30633 16.9131 6.69685 17.3036ZM12 19.5C12.5523 19.5 13 19.9477 13 20.5V22C13 22.5523 12.5523 23 12 23C11.4477 23 11 22.5523 11 22V20.5C11 19.9477 11.4477 19.5 12 19.5Z"
                  fill="currentColor"
                />
              </>
            }
            className={labelStyle({ center: true })}
            style={{ left: `${(sunriseHour / 24) * 100}%` }}
            size="s"
          />
          <Text
            variant="bodyXS"
            className={labelStyle({ center: true })}
            style={{ left: "50%" }}
            color="currentColor50"
          >
            12:00
          </Text>
          <Icon
            icon={
              <path
                d="M10.5163 2.04296C10.6958 2.27649 10.722 2.59345 10.5832 2.85326C9.99927 3.9469 9.66792 5.19607 9.66792 6.52485C9.66792 10.8367 13.1633 14.3321 17.4751 14.3321C18.8039 14.3321 20.0531 14.0007 21.1467 13.4168C21.4065 13.278 21.7235 13.3042 21.957 13.4837C22.1906 13.6632 22.2974 13.9627 22.2302 14.2495C21.1556 18.8349 17.0409 22.25 12.1269 22.25C6.39589 22.25 1.75 17.6041 1.75 11.8731C1.75 6.95906 5.16505 2.84445 9.7505 1.76979C10.0373 1.70258 10.3368 1.80944 10.5163 2.04296Z"
                fill="currentColor"
              />
            }
            className={labelStyle({ center: true })}
            style={{ left: `${(sunsetHour / 24) * 100}%` }}
            size="s"
          />
          <Text
            variant="bodyXS"
            className={labelStyle()}
            style={{ right: 0 }}
            color="currentColor50"
          >
            00:00
          </Text>
        </div>

        {/* Slider input */}
        <input
          type="range"
          value={value}
          onChange={(e) => {
            let hour = parseInt(e.target.value, 10);
            let minutes = 0;

            if (hour === 24) {
              hour = 23;
              minutes = 59;
            }

            onTimeChange(hour, minutes);
          }}
          min={0}
          max={24}
          step={1}
          aria-label="Tijd van de dag"
          className={sliderInputStyle}
        />
      </div>
    </>
  );
}
